/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { RootState } from '../../app/store';
import pearlApi from '../sharedcomponents/api/pearlApi';
import { snackNotifications } from '../sharedcomponents/notification/SnackBarUtils';

export type NPIState = {
  id: string;
  npiUserList: NpiUserListType[] | undefined;
  loadingUserList: boolean;
};

export type NpiUserListType = {
  full_name: string;
  address: string;
  number: string;
  first_specialty: string;
};

export const initialState: NPIState = {
  id: '',
  npiUserList: undefined,
  loadingUserList: false,
};

export const validateNPINumber = createAsyncThunk<
  NPIState,
  { id: string; type: string | undefined },
  { state: RootState }
>('validateNPINumber', async ({ id, type }) => {
  let query = type ? `profession=${id}` : `npi=${id}`;
  try {
    const {
      data: { data },
    } = await pearlApi.put(`/npi?${query}`);
    snackNotifications.success('NPI submitted successfully!');
    return data;
  } catch (err: any) {
    console.log(`Failed to validate NPI number: ${(err as Error).message}`);
  }
});
export const fetchNPIList = createAsyncThunk<NpiUserListType[], { query: string }, { state: RootState }>(
  'fetchNPIList',
  async ({ query }) => {
    try {
      const {
        data: { data },
      } = await pearlApi.get(`/npi?${query}`);
      return data;
    } catch (err: any) {
      snackNotifications.error(`Failed to fetch NPI List: ${err.title}`);
      console.log(`Failed to fetch NPI List: ${err.title}`);
    }
  }
);
export const remindNpiLater = createAsyncThunk<NPIState, { prompt_id: string | undefined }, { state: RootState }>(
  'remindNpiLater',
  async ({ prompt_id }) => {
    try {
      const {
        data: { data },
      } = await pearlApi.post(`/prompts/${prompt_id}/dismiss`);
      return data;
    } catch (err) {
      snackNotifications.error(`Failed to save: ${(err as Error).message}`);
    }
  }
);
export const npiSlice = createSlice({
  name: 'npi',
  initialState,
  reducers: {
    clearFetchNpiResults: (state) => {
      state.npiUserList = initialState.npiUserList;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchNPIList.pending, (state) => {
      state.loadingUserList = true;
    });
    builder.addCase(fetchNPIList.fulfilled, (state, data) => {
      state.npiUserList = data.payload;
      state.loadingUserList = false;
    });
    builder.addCase(fetchNPIList.rejected, (state) => {
      state.loadingUserList = false;
    });
  },
});

export const { clearFetchNpiResults } = npiSlice.actions;

export const selectNPIState = (state: RootState): NpiUserListType[] | undefined => state.npi.npiUserList;

export default npiSlice.reducer;
